import React, { useCallback, useMemo, useState } from 'react';
import { MultiTurnInteractionConfiguration, InteractionStepParams } from '@scale/shared/types/chat';
import { Accordion, Banner, Button, Icon, Typography, Tag } from '@scale/scaleui';
import { DragDropContext, Draggable, DropResult, Droppable } from 'react-beautiful-dnd';
import _isNil from 'lodash/isNil';
import StepJsonCreator from './StepJsonCreator';
import { getStepDescriptorByType } from '../../definitions';
import { produce } from 'immer';
import { StepSection } from '../v2/constants';
import { IChatTaskFeatureConfig } from '@scale/shared/types/config';

type Props = {
  value: MultiTurnInteractionConfiguration;
  onSubmit: (config: MultiTurnInteractionConfiguration) => void;
  property: 'before' | 'turn' | 'after' | 'qir';
  displayMLQualitySettings?: boolean;
  setForHeader?: boolean;
  navProps?: {
    currentSection?: StepSection;
    currentFieldId?: string;
  };
  setNavProps?: React.Dispatch<
    React.SetStateAction<{
      currentSection?: StepSection;
      currentFieldId?: string;
    }>
  >;
  chatTaskFeatureConfig?: IChatTaskFeatureConfig;
};

const StepSublistEditor = ({
  value,
  onSubmit,
  property,
  chatTaskFeatureConfig,
  displayMLQualitySettings,
  setForHeader = false,
  navProps,
  setNavProps,
}: Props) => {
  const [openAccordions, setOpenAccordions] = useState<number[]>([]);
  const [accordionTarget, setAccordionTarget] = useState<React.ReactNode | null>(null);

  const toggleAccordion = (index: number) => {
    setOpenAccordions(prevState =>
      prevState.includes(index) ? prevState.filter(i => i !== index) : [...prevState, index],
    );
  };

  const stageParams: InteractionStepParams[] = useMemo(() => {
    if (property === 'qir') return value[property] ?? [];
    return value[property];
  }, [value, property]);

  const stepsHiddenFromCustomer = useMemo(
    () => new Set(chatTaskFeatureConfig?.deliveryBuilderConfig?.stepsHiddenFromCustomer ?? []),
    [chatTaskFeatureConfig?.deliveryBuilderConfig?.stepsHiddenFromCustomer],
  );

  const handleDragEnd = useCallback(
    (result: DropResult) => {
      if (!result.destination) {
        return;
      }
      const startIndex = result.source.index;
      const endIndex = result.destination.index;
      const newStageParams = [...stageParams];
      const [removed] = newStageParams.splice(startIndex, 1);
      newStageParams.splice(endIndex, 0, removed);
      onSubmit({
        ...value,
        [property]: newStageParams,
      });
    },
    [stageParams, value, property, onSubmit],
  );

  const getItemStyle = (isDragging: boolean, draggableStyle: any) => ({
    userSelect: 'none',
    opacity: isDragging ? 0.1 : 1.0,
    ...draggableStyle,
  });

  if (_isNil(stageParams)) {
    return (
      <div className="flex">
        <Banner
          title={`Missing '${property}' stage`}
          subtitle={`This configuration does not have a '${property}' stage set. Please confirm that this chat project was correctly initialized.`}
          variant="danger"
        />
      </div>
    );
  }

  if (setForHeader && navProps && navProps.currentFieldId) {
    return <div>{accordionTarget}</div>;
  }

  return (
    <div className="flex flex-col gap-2">
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId={`droppable-${property}`}>
          {provided => (
            <div
              className="flex flex-col gap-2"
              {...provided.droppableProps}
              ref={provided.innerRef}
            >
              {stageParams.map((params, index) => {
                const step = getStepDescriptorByType(params.type);
                if (!step) {
                  throw new Error(
                    `Unknown step type at step id: ${params.id ?? 'No step id specified yet'}`,
                  );
                }
                let currentIndex = index;
                if (property === 'after') {
                  currentIndex += value.before.length + value.turn.length;
                } else if (property === 'turn') {
                  currentIndex += value.before.length;
                }
                const previousStepParams = [...value.before, ...value.turn, ...value.after].slice(
                  0,
                  currentIndex,
                );
                const isStepHidden = stepsHiddenFromCustomer.has(params.id);
                const AccordionTarget = (
                  <step.ConfigurationComponent
                    params={params}
                    previousParams={previousStepParams}
                    setParams={params => {
                      onSubmit({
                        ...value,
                        [property]: [
                          ...stageParams.slice(0, index),
                          params,
                          ...stageParams.slice(index + 1),
                        ],
                      });
                    }}
                    property={property}
                    taskParams={value}
                    displayMLQualitySettings={displayMLQualitySettings}
                    deliveryBuilderConfig={chatTaskFeatureConfig?.deliveryBuilderConfig}
                  />
                );

                const AccordionHeader = (
                  <div className="flex flex-col items-start gap-1">
                    <div className="flex items-center gap-2">
                      <div className="flex items-center">
                        {isStepHidden && (
                          <Icon
                            tooltip={
                              'Step is not visible to customers. To make it visible, make sure this step is checked on the "Customer Visibility" tab.'
                            }
                            icon="eye-slash"
                            size="sm"
                          />
                        )}
                        <Typography.Subtitle3>
                          Step {index + 1}: {step.title}
                        </Typography.Subtitle3>
                        <Icon tooltip={step.description} icon="info-circle" size="sm" />
                      </div>
                      {step.getConfigurationPreviewTags(params).map(tag => (
                        <Tag key={tag.key ?? tag.value} variant={tag.variant} icon={tag.icon}>
                          {tag.value}
                        </Tag>
                      ))}
                    </div>
                    <Typography.Subtitle4>{params.title}</Typography.Subtitle4>
                    <div className="flex items-center gap-1">
                      <Typography.Subtitle4>ID: {params.id}</Typography.Subtitle4>
                      <Icon
                        icon="copy"
                        size="sm"
                        tooltip="Copy Step ID"
                        onClick={e => {
                          e.stopPropagation();
                          navigator.clipboard.writeText(params.id);
                        }}
                      />
                    </div>
                  </div>
                );

                return (
                  <Draggable
                    key={`${params.id}-${params.title}-${index}}`}
                    draggableId={JSON.stringify(params)}
                    index={index}
                  >
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        className="flex items-center"
                        style={getItemStyle(snapshot.isDragging, provided.draggableProps.style)}
                      >
                        <div {...provided.dragHandleProps} style={{ cursor: 'grab' }}>
                          <Icon variant="neutral" size="md" icon="grip-vertical" />
                        </div>
                        {setForHeader ? (
                          <Button
                            onClick={() => {
                              if (navProps) {
                                setNavProps?.(
                                  produce(navProps, draft => {
                                    if (draft) {
                                      draft.currentFieldId = params.id;
                                    }
                                  }),
                                );
                              }
                              setAccordionTarget(AccordionTarget);
                            }}
                            variant="white"
                            className="flex-1 py-2 h-fit"
                          >
                            {AccordionHeader}
                          </Button>
                        ) : (
                          <Accordion
                            className={isStepHidden ? 'flex-1 bg-neutral-100' : 'flex-1'}
                            header={AccordionHeader}
                            target={AccordionTarget}
                            expanded={openAccordions.includes(index)}
                            onChange={() => toggleAccordion(index)}
                          />
                        )}

                        <div className="w-12 px-4">
                          <Icon
                            variant="neutral"
                            size="sm"
                            icon="trash"
                            onClick={() =>
                              onSubmit({
                                ...value,
                                [property]: [
                                  ...stageParams.slice(0, index),
                                  ...stageParams.slice(index + 1),
                                ],
                              })
                            }
                          />
                        </div>
                      </div>
                    )}
                  </Draggable>
                );
              })}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
      <div className="mt-2">
        <StepJsonCreator
          onSubmit={template => {
            onSubmit({
              ...value,
              [property]: [...stageParams, template],
            });
          }}
          property={property}
        />
      </div>
    </div>
  );
};

export default StepSublistEditor;
